"use client";

import React from "react";
import { ArrowRight, Compass, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background py-20 sm:py-28 lg:py-36">
      {/* Background Photography with Warm Architectural Overlay */}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_outdoor_screened_porch_patio.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_outdoor_screened_porch_patio.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_outdoor_screened_porch_patio.png-wvc-sizes" alt="Decatur custom screened porch with cedar timbers and stone patio" className="h-full w-full object-cover object-center" loading="eager" />
        <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] sm:bg-background/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Main Hero Card */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="rounded-2xl border border-border bg-card/85 p-6 shadow-xl backdrop-blur-md sm:p-10 lg:col-span-8 lg:p-12">
            {/* Architectural Copper Hairline Accent */}
            <div className="mb-6 h-0.5 w-16 bg-primary" />

            {/* Eyebrow Badge */}
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="outline" className="border-border bg-secondary/80 px-3 py-1 font-sans text-xs font-semibold tracking-wider text-secondary-foreground uppercase">
                Decatur Craftsmanship & Exterior Living
              </Badge>
            </motion.div>

            {/* Headline */}
            <motion.h1 variants={itemVariants} className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Outdoor Living & Deck Remodeling in Decatur
            </motion.h1>

            {/* Introductory Paragraph */}
            <motion.p variants={itemVariants} className="mt-6 max-w-2xl font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              Bespoke outdoor carpentry, custom screened porches, and patio
              living spaces engineered for Georgia climates. We blend timeless
              craftsmanship with durable materials to extend your home outdoors.
            </motion.p>

            {/* Primary Action and Trust Features */}
            <motion.div variants={itemVariants} className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="h-12 rounded-lg bg-primary px-8 font-sans font-semibold text-primary-foreground shadow-md transition-[color,background-color,border-color,box-shadow] duration-200 hover:bg-primary/90 hover:shadow-lg">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Request an Estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 rounded-lg border-border bg-background/90 px-6 font-sans font-semibold text-foreground transition-[color,background-color,border-color] duration-200 hover:bg-secondary">
                <Link to="/ProjectsPage">Explore Past Projects</Link>
              </Button>
            </motion.div>

            {/* Trust Markers Bar */}
            <motion.div variants={itemVariants} className="mt-10 grid grid-cols-1 gap-4 border-t border-border pt-6 sm:grid-cols-2">
              <div data-index={0} className="flex items-center gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
                  <Compass className="size-5" />
                </div>
                <div>
                  <p className="font-sans text-sm font-semibold text-foreground">
                    Rooted in Decatur
                  </p>
                  <p className="font-sans text-xs text-muted-foreground">
                    Serving Oakhurst, Winnona Park & Great Lakes
                  </p>
                </div>
              </div>

              <div data-index={1} className="flex items-center gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <p className="font-sans text-sm font-semibold text-foreground">
                    Built to Last
                  </p>
                  <p className="font-sans text-xs text-muted-foreground">
                    Careful craftsmanship and quality materials
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Side Feature Highlight Box */}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          y: shouldReduceMotion ? 0 : 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="hidden flex-col justify-between rounded-2xl border border-border bg-card/90 p-8 shadow-lg backdrop-blur-md lg:col-span-4 lg:flex">
            <div className="space-y-4">
              <span className="font-sans text-xs font-bold tracking-wider text-muted-foreground uppercase">
                Craftsmanship Scope
              </span>
              <h2 className="font-serif text-xl font-semibold text-foreground">
                Tailored for Georgia Living
              </h2>
              <p className="font-sans text-sm leading-relaxed text-muted-foreground">
                From covered cedar structures to bluestone patios, our team handles
                zoning, architectural design, structural framing, and final finishing.
              </p>
            </div>

            <div className="mt-8 border-t border-border pt-6">
              <Link to="/ProcessPlanningPage" className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-primary transition-colors hover:text-primary/80">
                <span>Read our Planning Guide</span>
                <ArrowRight className="size-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}